<template>
  <my-giving-layout :enable-scroll-event="true">
    <!-- Out of reach under the celebration's video: `inert` stops tabbing and reading
         behind it, and aria-hidden does the reading part where `inert` is unknown
         (iOS before 15.5, which the app still supports). -->
    <div
      class="hogc-theme-campaign bg-campaign-surface w-full min-h-full flex flex-col"
      :inert="celebrating || null"
      :aria-hidden="celebrating || null"
    >
      <!-- The banner's back goes to MyGiving, as Back to MyGiving did: the form this
           replaced is not behind it. The banner meets the top edge. -->
      <PledgeHeader :back="backToMyGiving" banner small :light="firstTimer"
        >You are a part<br />of history!</PledgeHeader
      >
      <div class="px-[16px] md:px-[24px] lg:px-[28px] pt-[24px] pb-[24px]">
        <div class="w-full md:max-w-2xl md:mx-auto">
          <PledgeSummary v-if="summary" :summary="summary" :show-first-time="firstTimer" />
        </div>
      </div>
      <!-- D4 and D5 (one-time) have no Dreams & Prayers: Give now closes the page. -->
      <div
        v-if="oneTime"
        class="mt-auto w-full md:max-w-2xl md:mx-auto px-[16px] md:px-[24px] lg:px-[28px] pb-[max(32px,calc(env(safe-area-inset-bottom)+16px))]"
      >
        <DsButton
          variant="campaign"
          size="52"
          class="w-full"
          data-test="bf-done-give-now"
          @click="giveNow"
        >
          Give now
        </DsButton>
      </div>
      <DreamsPrayers v-else>
        <div class="flex flex-col gap-[14px]">
          <DsButton
            variant="campaign"
            size="52"
            class="w-full"
            data-test="bf-done-give-now"
            @click="giveNow"
          >
            Give now
          </DsButton>
          <!-- D2 alone keeps a way back, as a plain text action. -->
          <button
            v-if="sowOnly"
            type="button"
            class="h-[52px] w-full border-0 bg-transparent text-[16px] font-bold text-campaign-primary"
            data-test="bf-done-back"
            @click="backToMyGiving"
          >
            Back to MyGiving
          </button>
        </div>
      </DreamsPrayers>
    </div>
    <PledgeSubmittedVideo v-if="celebrating" :key="entry" @continue="endCelebration" />
  </my-giving-layout>
</template>

<script>
import { Button as DsButton } from '@team-hogc/hogc-one-design-system';
import MyGivingLayout from '@/components/MyGivingLayout.vue';
import DreamsPrayers from '@/components/MyGiving/BuildingFundPledge/DreamsPrayers.vue';
import PledgeSummary from '@/components/MyGiving/BuildingFundPledge/PledgeSummary.vue';
import PledgeHeader from '@/components/MyGiving/BuildingFundPledge/PledgeHeader.vue';
import PledgeSubmittedVideo from '@/components/MyGiving/BuildingFundPledge/PledgeSubmittedVideo.vue';
import { BF_GIVE_NOW_FUND } from '@/constants/MyGiving/building-fund-pledge';
import { bfGiveNowRoute, isBfCelebration, pledgeSummary } from '@/utils/building-fund-pledge';
import {
  campaignFirstStatusBar,
  claimStatusBar,
  releaseStatusBar,
} from '@/utils/status-bar-claims';

const STATUS_BAR_OWNER = 'bf-pledge-first';

// D1–D5 of the BF2026 digital pledge: what the member just submitted. giving-api
// answers a submit with an empty 201, so this renders the body the store kept in
// `submitted`; the route guard sends anyone without one to the view page. Give now
// is the way on, with a one-time pledge's amount filled in.
export default {
  name: 'BuildingFundPledgeDone',

  components: {
    MyGivingLayout,
    DreamsPrayers,
    DsButton,
    PledgeSummary,
    PledgeHeader,
    PledgeSubmittedVideo,
  },

  data() {
    return {
      summary: null,
      // The celebration (BF_PLEDGE_CELEBRATION_FLAG): true while the video is up.
      // Read here as well as on entry so the page's first frame is already under
      // the video's navy.
      celebrating: isBfCelebration(),
      // Counts entries, to key the video: left without Continue, this kept-alive page
      // still has the last one up, ended, and a new pledge must not reuse it.
      entry: 0,
    };
  },

  computed: {
    oneTime() {
      return this.summary?.oneTime > 0;
    },

    sowOnly() {
      return Boolean(this.summary?.sow) && !this.summary.total && !this.oneTime;
    },

    // A first-timer's page: light, with their tick (D1b, D3b, D4b). D5 (one-time
    // and sow) has no first-timer frame, so it keeps the usual page.
    firstTimer() {
      return (
        Boolean(this.summary?.firstTime) &&
        (this.summary.total > 0 || (this.oneTime && !this.summary.sow))
      );
    },
  },

  // A copy, taken on every entry (Ionic keeps this page alive between visits), so
  // clearing `submitted` on the way out cannot blank the page mid-transition.
  ionViewWillEnter() {
    const submitted = this.$store.state.buildingFundPledge.submitted;
    if (submitted) this.summary = pledgeSummary(submitted);
    this.celebrating = isBfCelebration();
    this.entry += 1;
  },

  // The router claims the navy status bar for every pledge route as it navigates,
  // so the light page claims after it: the newest claim wins. Not under the video,
  // which is navy too: the light bar waits for it to go.
  ionViewDidEnter() {
    if (!this.celebrating) this.claimFirstTimerBar();
  },

  ionViewWillLeave() {
    releaseStatusBar(STATUS_BAR_OWNER);
  },

  beforeUnmount() {
    releaseStatusBar(STATUS_BAR_OWNER);
  },

  methods: {
    claimFirstTimerBar() {
      if (this.firstTimer) claimStatusBar(STATUS_BAR_OWNER, campaignFirstStatusBar());
    },

    // The video has faded away: the page is the member's.
    endCelebration() {
      this.celebrating = false;
      this.claimFirstTimerBar();
    },

    // Done with it: the landing banner reads `pledge`, not this, and leaving it
    // would keep the pledge reachable at /done for the rest of the session.
    leave(route) {
      this.$store.commit('buildingFundPledge/setSubmitted', null);
      this.$router.replace(route);
    },

    backToMyGiving() {
      this.leave('/my-giving');
    },

    // Give now with Building Fund chosen. After a one-time pledge its amount is
    // filled in too, kept in the store, never in the route.
    giveNow() {
      if (this.oneTime) {
        this.$store.commit('myGiving/setGiveNowPrefill', {
          fund: BF_GIVE_NOW_FUND,
          amount: this.summary.oneTime,
        });
      }
      this.leave(bfGiveNowRoute({ prefill: this.oneTime }));
    },
  },
};
</script>
